import React, { useCallback } from "react";

import { Accordion, useAccordionSingleMode } from "src/design-system/Accordion";
import { DetectedEntitySection } from "src/featureCatalogue/Queries/Create/DetectedEntitySection";
import { FeatureSection } from "src/featureCatalogue/Queries/Create/FeatureSection";
import {
  ColumnType,
  PreviewData,
} from "src/featureCatalogue/Queries/Create/helpers";
import { QuerySection } from "src/featureCatalogue/Queries/Create/QuerySection";
import { UseFeatureQueryPreview } from "src/featureCatalogue/queries";

const QUERY_ITEM = "sql-query";
const FEATURES_ITEM = "define-features";

type FeatureQueryAccordionProps = {
  readOnly: boolean;
  featureQueryPreview: UseFeatureQueryPreview;
  handleFeatureGeneration: (previewData: PreviewData) => boolean;
  hasFeatures: boolean;
  columnTypes: Record<string, ColumnType>;
  isLoading: boolean;
  isEditMode: boolean;
};

export const FeatureQueryAccordion: React.FC<FeatureQueryAccordionProps> = ({
  readOnly,
  featureQueryPreview,
  handleFeatureGeneration,
  hasFeatures,
  columnTypes,
  isLoading,
  isEditMode,
}) => {
  const { value, onChange } = useAccordionSingleMode(QUERY_ITEM);

  const handleFeatureGenerationWithCollapse = useCallback(
    (previewData: PreviewData) => {
      const hasGeneratedFeatures = handleFeatureGeneration(previewData);
      if (hasGeneratedFeatures) {
        onChange([FEATURES_ITEM]);
      }
    },
    [handleFeatureGeneration, onChange],
  );

  // When query accordion is closed, we open it when changes are applied from copilot.
  const expandQuerySection = useCallback(() => {
    if (!value.includes(QUERY_ITEM)) {
      onChange([QUERY_ITEM]);
    }
  }, [value, onChange]);

  const hasSettledPreviewWithFeatures =
    hasFeatures &&
    (isLoading ||
      featureQueryPreview.isIdle ||
      (!featureQueryPreview.isPending &&
        !featureQueryPreview.error &&
        featureQueryPreview.isSuccess));

  return (
    <Accordion
      dataLoc="features-query-accordion"
      value={value}
      onChange={onChange}
    >
      <QuerySection
        featureQueryPreview={featureQueryPreview}
        handleFeatureGeneration={handleFeatureGenerationWithCollapse}
        isEditMode={isEditMode}
        isLoading={isLoading}
        readOnly={readOnly}
        onExpandQuerySection={expandQuerySection}
      />
      {hasSettledPreviewWithFeatures && (
        <DetectedEntitySection
          isEditMode={isEditMode}
          isLoading={isLoading}
          columnTypes={columnTypes}
          readOnly={readOnly}
        />
      )}
      {hasSettledPreviewWithFeatures && (
        <FeatureSection
          columnTypes={columnTypes}
          featureQueryPreview={featureQueryPreview}
          isLoading={isLoading}
          readonly={readOnly}
        />
      )}
    </Accordion>
  );
};
